/**
 * Bridges PostHog Error Tracking onto the noise filtering and origin
 * classification Panda already runs for Sentry.
 *
 * Enabling `capture_exceptions` sends the same browser exceptions to a second
 * tool. That was the standing objection to turning it on (see
 * docs/failure-monitoring.md): the four documented junk classes dropped in
 * instrumentation-client.ts — Meta in-app-browser bridge errors, browser
 * translation DOM mutations, cross-origin frame reads from third-party scripts,
 * and embedded-crawler CefSharp rejections — plus the split between a Panda
 * shell crash and a crash inside the user's GENERATED app running in the
 * preview sandbox, would otherwise have to be rebuilt inside PostHog.
 *
 * A fifth class is dropped here but NOT in instrumentation-client.ts: the
 * browser's opaque cross-origin `Script error.`. Sentry filters that one out of
 * the box (DEFAULT_IGNORE_ERRORS), so it needs no entry there — and adding one
 * would be dead code, since the SDK drops the event in an event processor that
 * runs before `beforeSend`. PostHog has no such default, which is the entire
 * reason it accumulated an issue Sentry never showed.
 * See opaque-cross-origin-script-noise.ts.
 *
 * They don't. Every one of those predicates was written against a minimal
 * STRUCTURAL shape (`{ message, exception: { values: [{ type, value, stacktrace:
 * { frames } }] } }`) rather than against a Sentry import, precisely so a second
 * capture path could reuse them. This module is that path: it reshapes a
 * PostHog `$exception` event into that structure and runs the identical
 * decision, so both tools drop the same noise and agree on origin.
 *
 * Pure and dependency-free (no posthog-js import) so it is trivially
 * unit-testable and safe to evaluate inside `before_send`, which runs before
 * the SDK is fully ready.
 */

import {
  classifyErrorOrigin,
  GENERATED_APP_PREVIEW_ORIGIN,
  type ErrorOrigin,
} from "./error-origin";
import { isInAppBrowserBridgeNoise } from "./in-app-browser-noise";
import {
  isExternalDomMutationNoise,
  DOM_MUTATION_EXTERNAL_ORIGIN,
} from "./dom-mutation-noise";
import {
  isExternalCrossOriginFrameNoise,
  CROSS_ORIGIN_FRAME_EXTERNAL_ORIGIN,
} from "./cross-origin-frame-noise";
import {
  isEmbeddedCrawlerNoise,
  EMBEDDED_CRAWLER_ORIGIN,
} from "./embedded-crawler-noise";
import {
  isOpaqueCrossOriginScriptNoise,
  OPAQUE_CROSS_ORIGIN_SCRIPT_ORIGIN,
} from "./opaque-cross-origin-script-noise";

/** PostHog's event name for an Error Tracking exception. */
export const POSTHOG_EXCEPTION_EVENT = "$exception" as const;

/**
 * A stack frame as posthog-js's exception autocapture emits it. `filename` is
 * always the frame source (the classifiers read it first); `abs_path`/`module`
 * are Sentry-only and simply absent here, which every predicate tolerates.
 */
interface PostHogStackFrame {
  filename?: string;
  function?: string;
  lineno?: number;
  colno?: number;
  in_app?: boolean;
  platform?: string;
}

/** One entry of `$exception_list` — PostHog's analogue of `exception.values[]`. */
export interface PostHogExceptionListEntry {
  type?: string;
  value?: string;
  mechanism?: { handled?: boolean; synthetic?: boolean; type?: string };
  stacktrace?: { type?: string; frames?: PostHogStackFrame[] };
}

/**
 * The subset of a posthog-js `CaptureResult` this module reads. A real
 * `CaptureResult` satisfies it structurally, so nothing needs casting at the
 * `before_send` call site.
 */
export interface PostHogExceptionCaptureResult {
  event?: string;
  properties?: Record<string, unknown> & {
    $exception_list?: unknown;
    $exception_message?: unknown;
    $exception_type?: unknown;
  };
}

/** True when this capture result is an Error Tracking exception event. */
export function isPostHogExceptionEvent(
  cr: PostHogExceptionCaptureResult | null | undefined,
): boolean {
  return !!cr && cr.event === POSTHOG_EXCEPTION_EVENT;
}

function asExceptionList(raw: unknown): PostHogExceptionListEntry[] {
  return Array.isArray(raw) ? (raw as PostHogExceptionListEntry[]) : [];
}

/**
 * Reshape a PostHog `$exception` into the structural event the Sentry-era
 * predicates consume. `$exception_list` maps 1:1 onto `exception.values`, and
 * `$exception_message` becomes the top-level `message` that the two
 * message-only classifiers (DOM mutation, embedded crawler) fall back to when a
 * rejection carries no frames at all.
 */
export function toClassifierEvent(cr: PostHogExceptionCaptureResult): {
  message?: unknown;
  exception: { values: PostHogExceptionListEntry[] };
  tags: { error_origin?: unknown };
} {
  const props = cr.properties ?? {};
  return {
    message: props.$exception_message,
    exception: { values: asExceptionList(props.$exception_list) },
    // Mirrors the Sentry path: an origin an upstream layer already stamped
    // (the preview-frame error relay hand-forwarding a generated-app crash)
    // is honoured, so a forwarded error is classified correctly even when it
    // carries no sandbox stack frames of its own.
    tags: { error_origin: props.error_origin },
  };
}

/**
 * Why an exception was dropped, or the origin it was tagged with. Mirrors the
 * `error_origin` tag values used on the Sentry path so a triage question asked
 * of one tool can be asked of the other with the same vocabulary.
 */
export type PostHogExceptionVerdict =
  | { drop: true; origin: string }
  | { drop: false; origin: ErrorOrigin };

/**
 * The single triage decision for a PostHog exception, identical in behaviour to
 * the `beforeSend` chain in instrumentation-client.ts.
 *
 * Dropped: the four third-party noise classes Sentry's `beforeSend` also drops,
 * plus the opaque cross-origin `Script error.` Sentry drops by default. Kept:
 * everything else, tagged
 * `panda-shell` or `generated-app-preview`. Generated-app crashes are kept
 * (they are real bugs in a real user's app) but must never merge into a Panda
 * shell issue — the caller fingerprints them separately.
 *
 * Fail-safe by contract: any unexpected shape or thrown predicate keeps the
 * event as a shell error. Losing a real crash is worse than keeping noise.
 */
export function classifyPostHogException(
  cr: PostHogExceptionCaptureResult,
): PostHogExceptionVerdict {
  try {
    const event = toClassifierEvent(cr);
    if (isInAppBrowserBridgeNoise(event)) {
      return { drop: true, origin: "in-app-browser-bridge" };
    }
    if (isExternalDomMutationNoise(event)) {
      return { drop: true, origin: DOM_MUTATION_EXTERNAL_ORIGIN };
    }
    if (isExternalCrossOriginFrameNoise(event)) {
      return { drop: true, origin: CROSS_ORIGIN_FRAME_EXTERNAL_ORIGIN };
    }
    if (isEmbeddedCrawlerNoise(event)) {
      return { drop: true, origin: EMBEDDED_CRAWLER_ORIGIN };
    }
    // The browser's muted cross-origin `Script error.` — no message, no source,
    // no stack, by design. Sentry drops this via its DEFAULT_IGNORE_ERRORS list
    // before `beforeSend` runs, which is why it only ever surfaced on the
    // PostHog path. Third-party tags that CAN be attributed now load with
    // `crossOrigin="anonymous"`, so what reaches here is the residue no code
    // change can un-mute. See opaque-cross-origin-script-noise.ts.
    //
    // Ordered BEFORE `classifyErrorOrigin` on purpose, which means a muted error
    // relayed out of the preview sandbox is dropped rather than kept and tagged
    // `generated-app-preview`. That is the right trade: the generated-app bucket
    // exists so a user's real app bug stays visible and separable, and a husk
    // with no message, file or stack is not a bug anyone can act on — in that
    // bucket it would only be unresolvable clutter. The generated app's own
    // build/runtime failures reach the user through the preview pipeline
    // (preview-error-type.ts), not through Error Tracking, so nothing the user
    // needs to see is lost here.
    if (isOpaqueCrossOriginScriptNoise(event)) {
      return { drop: true, origin: OPAQUE_CROSS_ORIGIN_SCRIPT_ORIGIN };
    }
    return { drop: false, origin: classifyErrorOrigin(event) };
  } catch {
    return { drop: false, origin: "panda-shell" };
  }
}

export { GENERATED_APP_PREVIEW_ORIGIN };
